import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  LogOut, Users, UserCheck, IndianRupee, TrendingUp,
  Download, Trash2, MessageCircle, RefreshCw, Search,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUSES = ["New", "Contacted", "Enrolled", "Closed"];
const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  Contacted: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  Enrolled: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  Closed: "bg-slate-200 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300",
};
const PIE_COLORS = ["#0D5C53", "#E86A33", "#D97706", "#3B82F6", "#8B5CF6"];

export default function AdminDashboard() {
  const [enquiries, setEnquiries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [eq, st, pay] = await Promise.all([api.get("/admin/enquiries"), api.get("/admin/stats"), api.get("/admin/payments")]);
      setEnquiries(eq.data);
      setStats(st.data);
      setPayments(pay.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("khoj_admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    document.title = "Dashboard — TheKhoj Admin";
    if (!localStorage.getItem("khoj_admin_token")) {
      navigate("/admin");
      return;
    }
    load();
  }, [load, navigate]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((prev) => prev.map((e) => (e.enquiry_id === id ? { ...e, status } : e)));
      const st = await api.get("/admin/stats");
      setStats(st.data);
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { notes });
      toast.success("Note saved");
    } catch {
      toast.error("Could not save note");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setEnquiries((prev) => prev.filter((e) => e.enquiry_id !== id));
      const st = await api.get("/admin/stats");
      setStats(st.data);
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/enquiries-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "thekhoj_enquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("khoj_admin_token");
    navigate("/admin");
  };

  const filtered = enquiries.filter((e) => {
    const q = search.toLowerCase();
    const matchQ = !q || [e.student_name, e.parent_name, e.phone, e.email].some((v) => v?.toLowerCase().includes(q));
    return matchQ && (!gradeFilter || e.grade === gradeFilter) && (!statusFilter || e.status === statusFilter);
  });

  const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full overflow-hidden ring-1 ring-border">
              <img src="/logos/compass_mark.png" alt="TheKhoj compass logo" className="w-full h-full object-cover" />
            </span>
            <div>
              <p className="font-display font-extrabold text-xs sm:text-sm leading-tight">TheKhoj Business Dashboard</p>
              <p className="hidden sm:block text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Enquiries · Revenue · Growth</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors" data-testid="admin-refresh-button" aria-label="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={exportCsv} className="hidden sm:inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full border border-border hover:bg-secondary transition-colors" data-testid="admin-export-csv-button">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <a href="/" className="text-xs font-medium text-muted-foreground hover:text-foreground px-2" data-testid="admin-view-site-link">View Site</a>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full bg-primary text-primary-foreground hover:opacity-90" data-testid="admin-logout-button">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {loading ? (
          <p className="text-center text-muted-foreground py-20" data-testid="admin-loading">Loading dashboard…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Enquiries", value: stats?.total ?? 0, icon: Users, testid: "admin-metrics-total-queries" },
                { label: "Paid Enrolments", value: stats?.paid_enrolments ?? 0, icon: UserCheck, testid: "admin-metrics-enrolled" },
                { label: "Collected Revenue", value: inr(stats?.collected_revenue), icon: IndianRupee, testid: "admin-metrics-revenue" },
                { label: "Pipeline Value", value: inr(stats?.pipeline_value), icon: TrendingUp, testid: "admin-metrics-pipeline" },
              ].map((m) => (
                <div key={m.testid} className="bg-card border border-border rounded-2xl p-5" data-testid={m.testid}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-muted-foreground">{m.label}</p>
                    <m.icon className="w-4 h-4 text-primary" />
                  </div>
                  <p className="font-display text-2xl sm:text-3xl font-extrabold">{m.value}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-2xl p-6" data-testid="admin-chart-monthly">
                <p className="font-display font-bold mb-1">Monthly Enquiries Trend</p>
                <p className="text-xs text-muted-foreground mb-5">How interest is scaling, month over month</p>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={stats?.monthly_trend || []}>
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="currentColor" opacity={0.5} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="currentColor" opacity={0.5} />
                    <Tooltip cursor={{ fill: "transparent" }} />
                    <Bar dataKey="enquiries" fill="#0D5C53" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-card border border-border rounded-2xl p-6" data-testid="admin-chart-grades">
                <p className="font-display font-bold mb-1">Grade Distribution</p>
                <p className="text-xs text-muted-foreground mb-5">Which grades are reaching out the most</p>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={stats?.by_grade || []} dataKey="count" nameKey="grade" innerRadius={55} outerRadius={85} paddingAngle={3}>
                      {(stats?.by_grade || []).map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="admin-enquiries-table">
              <div className="p-6 border-b border-border flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
                <div>
                  <p className="font-display font-bold">Enquiries</p>
                  <p className="text-xs text-muted-foreground">{filtered.length} of {enquiries.length} shown · Conversion rate {stats?.conversion_rate ?? 0}%</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, email…"
                      className="pl-9 pr-4 py-2.5 rounded-full border border-input bg-background text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-ring"
                      data-testid="admin-search-input"
                    />
                  </div>
                  <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} className="px-4 py-2.5 rounded-full border border-input bg-background text-sm" data-testid="admin-grade-filter">
                    <option value="">All Grades</option>
                    {["Grade 9", "Grade 10", "Grade 11", "Grade 12"].map((g) => <option key={g}>{g}</option>)}
                  </select>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2.5 rounded-full border border-input bg-background text-sm" data-testid="admin-status-filter">
                    <option value="">All Statuses</option>
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {filtered.length === 0 ? (
                <p className="text-center text-muted-foreground py-16 text-sm" data-testid="admin-empty-state">
                  No enquiries yet. Share your website link to start receiving them.
                </p>
              ) : (
                <div className="divide-y divide-border">
                  {filtered.map((e) => (
                    <div key={e.enquiry_id} className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start" data-testid={`admin-enquiry-row-${e.enquiry_id.slice(0, 8)}`}>
                      <div className="lg:col-span-3">
                        <p className="font-semibold text-sm">{e.student_name} <span className="text-xs text-muted-foreground font-normal">· {e.grade}</span></p>
                        <p className="text-xs text-muted-foreground mt-0.5">{e.relation}: {e.parent_name}</p>
                        <p className="text-[11px] text-muted-foreground font-mono mt-1">{new Date(e.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                      </div>
                      <div className="lg:col-span-3 text-xs text-muted-foreground space-y-1">
                        <p>{e.phone}</p>
                        <p className="break-all">{e.email}</p>
                        {e.message && <p className="italic">“{e.message}”</p>}
                      </div>
                      <div className="lg:col-span-3">
                        <input
                          defaultValue={e.notes || ""}
                          placeholder="Add follow-up note…"
                          onBlur={(ev) => ev.target.value !== (e.notes || "") && saveNotes(e.enquiry_id, ev.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                          data-testid={`admin-notes-input-${e.enquiry_id.slice(0, 8)}`}
                        />
                      </div>
                      <div className="lg:col-span-3 flex flex-wrap items-center gap-2 justify-end">
                        <select
                          value={e.status}
                          onChange={(ev) => updateStatus(e.enquiry_id, ev.target.value)}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full border-0 cursor-pointer ${STATUS_COLORS[e.status]}`}
                          data-testid={`admin-status-select-${e.enquiry_id.slice(0, 8)}`}
                        >
                          {STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                        <a
                          href={`https://wa.me/${e.phone.replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}?text=${encodeURIComponent(`Hi ${e.parent_name}, this is TheKhoj following up on your career exploration enquiry for ${e.student_name}.`)}`}
                          target="_blank" rel="noopener noreferrer"
                          className="w-8 h-8 rounded-full bg-[#25D366]/15 text-[#1da851] flex items-center justify-center hover:bg-[#25D366]/25 transition-colors"
                          data-testid={`admin-whatsapp-${e.enquiry_id.slice(0, 8)}`}
                          aria-label="WhatsApp"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => remove(e.enquiry_id)}
                          className="w-8 h-8 rounded-full bg-destructive/10 text-destructive flex items-center justify-center hover:bg-destructive/20 transition-colors"
                          data-testid={`admin-delete-${e.enquiry_id.slice(0, 8)}`}
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="admin-payments-table">
              <div className="p-6 border-b border-border">
                <p className="font-display font-bold">Payments</p>
                <p className="text-xs text-muted-foreground">
                  {payments.filter((p) => p.payment_status === "paid").length} paid · {inr(stats?.collected_revenue)} collected
                </p>
              </div>
              {payments.length === 0 ? (
                <p className="text-center text-muted-foreground py-12 text-sm" data-testid="admin-payments-empty">
                  No payments yet. Share your website — parents can pay directly from the Pricing section.
                </p>
              ) : (
                <div className="divide-y divide-border">
                  {payments.map((p) => (
                    <div key={p.session_id} className="p-5 sm:px-6 flex flex-wrap items-center gap-4 justify-between" data-testid={`admin-payment-row-${p.session_id.slice(-8)}`}>
                      <div>
                        <p className="font-semibold text-sm">{p.student_name || "—"}</p>
                        <p className="text-xs text-muted-foreground">{p.parent_email || "—"}</p>
                        <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-display font-bold">{inr(p.amount)}</p>
                        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${
                          p.payment_status === "paid"
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                            : p.payment_status === "pending"
                              ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                              : "bg-slate-200 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300"
                        }`} data-testid={`admin-payment-status-${p.session_id.slice(-8)}`}>
                          {p.payment_status === "paid" ? "Paid" : p.payment_status === "pending" ? "Pending" : "Failed"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
